import { useState, useEffect } from "react";
import Papa from "papaparse";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Upload, Loader2, FileSpreadsheet } from "lucide-react";

const OUR_FIELDS = [
  { key: "name", label: "Nombre *", aliases: ["name", "nombre"] },
  { key: "description", label: "Descripción", aliases: ["description", "descripcion", "tagline"] },
  { key: "country", label: "País", aliases: ["country", "pais"] },
  { key: "city", label: "Ciudad", aliases: ["city", "ciudad"] },
  { key: "website", label: "Web", aliases: ["web", "website", "url"] },
  { key: "type", label: "Tipo de Agente", aliases: ["type", "tipo"] },
  { key: "specialty", label: "Especialidad", aliases: ["specialty", "especialidad"] },
  { key: "founded", label: "Fundación", aliases: ["founded", "fundacion"] },
  { key: "employees", label: "Empleados", aliases: ["employees", "empleados"] },
  { key: "fit_negocio", label: "Fit negocio (UN)", aliases: ["un", "fitnegocio", "fit negocio"] },
  { key: "origin", label: "Origen", aliases: ["origin", "origen"] },
  { key: "event_leads", label: "Event Leads", aliases: ["eventleads", "event leads"] },
  { key: "batch", label: "Batch", aliases: ["batch", "coleccion"] },
  { key: "pipeline", label: "Pipeline", aliases: ["pipeline"] },
  { key: "status", label: "Status", aliases: ["status"] },
  { key: "contacts", label: "Contactos", aliases: ["contacts", "contactos"] },
  { key: "info_adicional", label: "Info adicional", aliases: ["infoadicional", "info. adicional", "info adicional"] },
];

// Migración Airtable → OS: subir CSV exportado de Airtable (mismas columnas,
// sin reordenar/reinterpretar) + pantalla de mapeo 1:1. Feedback Ecosistema/
// Partners 2026-08-31 P0 punto 6 — "traer la BBDD aquí".
export function CsvMigrationDialog({ open, onOpenChange, onImported }) {
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setStep(1); setHeaders([]); setRows([]); setMapping({}); setResult(null); };

  // Fix (testing_agent iter108): el botón "Cerrar" del paso 3 llama a onOpenChange
  // del padre directamente, sin pasar por el onOpenChange del <Dialog> — así que
  // reset() nunca se ejecutaba y al reabrir se veía el resultado anterior (step 3)
  // sin input de archivo. Resetear siempre que `open` pase a false, sea por donde sea.
  useEffect(() => { if (!open) reset(); }, [open]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const hdrs = res.meta.fields || [];
        setHeaders(hdrs);
        setRows(res.data);
        const autoMap = {};
        OUR_FIELDS.forEach((f) => {
          const norm = (s) => s.toLowerCase().replace(/[^a-z]/g, "");
          const match = hdrs.find((h) => f.aliases.some((a) => norm(a) === norm(h)));
          if (match) autoMap[f.key] = match;
        });
        setMapping(autoMap);
        setStep(2);
      },
      error: () => toast.error("No se pudo leer el CSV"),
    });
  };

  const handleImport = async () => {
    if (!mapping.name) { toast.error("Mapea al menos la columna Nombre"); return; }
    setImporting(true);
    try {
      const res = await api.importPartnersCsv(token, { mapping, rows });
      setResult(res);
      setStep(3);
      onImported?.();
    } catch {
      toast.error("Error importando el CSV");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="csv-migration-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" />Migrar desde CSV (Airtable → OS)</DialogTitle>
          <DialogDescription>Sube el CSV exportado de la tabla Ecosystem de Airtable, sin reordenar columnas. Luego mapea cada columna 1:1.</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="py-8 flex flex-col items-center gap-3 border-2 border-dashed rounded-lg">
            <Upload className="w-8 h-8 text-muted-foreground" />
            <label className="cursor-pointer">
              <span className="text-sm text-primary underline">Selecciona un archivo CSV</span>
              <input type="file" accept=".csv" className="hidden" onChange={handleFile} data-testid="csv-migration-file-input" />
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{rows.length} filas detectadas, {headers.length} columnas. Confirma el mapeo antes de importar.</p>
            <div className="max-h-80 overflow-y-auto space-y-2">
              {OUR_FIELDS.map((f) => (
                <div key={f.key} className="grid grid-cols-2 gap-3 items-center">
                  <span className="text-sm font-medium">{f.label}</span>
                  <Select value={mapping[f.key] || "__none"} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "__none" ? "" : v }))}>
                    <SelectTrigger data-testid={`csv-mapping-${f.key}`}><SelectValue placeholder="Sin mapear" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Sin mapear</SelectItem>
                      {headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={reset}>Volver a subir</Button>
              <Button type="button" onClick={handleImport} disabled={importing} data-testid="csv-migration-confirm-btn">
                {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Importar {rows.length} filas
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-3" data-testid="csv-migration-result">
            <p className="text-sm">Importación completa: <strong>{result.created}</strong> creados, <strong>{result.updated}</strong> actualizados, {result.skipped} sin nombre (omitidos), de {result.total_rows} filas.</p>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} data-testid="csv-migration-close-btn">Cerrar</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
